// Artifact Labs — 3D shelf storefront for Shopify
// Reads products from the <script id="artifact-data"> JSON rendered by sections/shelf.liquid
// and talks to Shopify's Ajax Cart API (/cart.js, /cart/add.js, /cart/change.js).
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { STONES, stoneTextures, stoneMaterial, ancientWood, plasterWall, buildGoddess } from './artifact-materials.js';

const root = document.getElementById('artifact-app');
const DATA = JSON.parse(document.getElementById('artifact-data').textContent);
const S = DATA.settings || {};
const ROOT_URL = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
const SPACING = 1.2;

/* ---------- helpers ---------- */
const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const money = cents => new Intl.NumberFormat(DATA.locale || undefined, {
  style: 'currency', currency: DATA.currency || 'USD', minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2,
}).format(cents / 100);
const pad = n => String(n).padStart(2, '0');
function stoneKey(name){
  const n = String(name || '').toLowerCase();
  if (/basalt|black|nero|onyx|obsidian|slate/.test(n)) return 'basalt';
  if (/travertin|limestone|ivory|cream/.test(n)) return 'travertine';
  if (/sand|desert|terracotta|ochre|gold/.test(n)) return 'sandstone';
  if (/marble|carrara|vein/.test(n)) return 'marble';
  return 'chalk';
}
const POSES = ['athena', 'aphrodite', 'artemis', 'nike', 'hera', 'persephone'];

/* ---------- products ---------- */
const PRODUCTS = (DATA.products || []).map((p, i) => {
  const fi = p.options.findIndex(o => /finish|stone|material|colou?r/i.test(o));
  const si = p.options.findIndex(o => /height|size/i.test(o));
  const uniq = k => k < 0 ? [] : [...new Set(p.variants.map(v => v.options[k]))];
  const t = (p.handle + ' ' + p.title).toLowerCase();
  return {
    ...p, fi, si, finishes: uniq(fi), sizes: uniq(si),
    pose: POSES.find(k => t.includes(k)) || POSES[i % POSES.length],
    minPrice: Math.min(...p.variants.map(v => v.price)),
  };
});
const findVariant = (p, finish, size) =>
  p.variants.find(v => (p.fi < 0 || v.options[p.fi] === finish) && (p.si < 0 || v.options[p.si] === size)) || p.variants[0];
const defaultStone = p => p.stone ? stoneKey(p.stone) : p.finishes.length ? stoneKey(p.finishes[0]) : 'chalk';
// bundle tiers from Theme settings, e.g. "1 sculpture: $65 | 2 sculptures: $100 | 3 sculptures: $120"
// Two formats: fixed bundle prices "2 sculptures: $90" or percentage tiers "2 sculptures: 20%".
// Percentage tiers follow whichever size is selected and work for any mix of sizes in the cart.
const TIERS = String(S.tiers || '').split('|').map(t => {
  const [label, price] = t.split(':').map(x => (x || '').trim());
  const n = parseInt(label, 10), val = parseFloat(String(price).replace(/[^0-9.]/g, ''));
  if (!(n > 0) || isNaN(val)) return null;
  if (String(price).includes('%')) return { n, label, pct: val };
  const cents = Math.round(val * 100);
  return cents > 0 ? { n, label, cents, each: Math.round(cents / n) } : null;
}).filter(Boolean).sort((a, b) => a.n - b.n);
const PCT = TIERS.length > 0 && TIERS.every(t => t.pct !== undefined);
// price of n pieces at a given single-piece price
const tierTotal = (t, unit) => PCT ? Math.round(unit * t.n * (1 - t.pct / 100)) : t.cents;

/* ---------- DOM ---------- */
const logo = S.logoImage
  ? `<img class="logo-img" src="${esc(S.logoImage)}" alt="${esc(S.logoText)}">${S.logoShowText === false ? '' : `<span class="logo-code">${esc(S.logoText || 'ARTIFACT LABS')}</span>`}`
  : `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.1"><path d="M3 21h18M5 21V10M19 21V10M9 21V10M15 21V10M2.5 10h19L12 3z"/></svg><span>${esc(S.logoText || 'ARTIFACT LABS')}</span>`;
root.innerHTML = `
<div id="loader"><span>${esc(S.logoText || 'ARTIFACT LABS')}</span></div>
<header>
  <div class="nav-left">${esc(S.heading)}</div>
  <a class="logo${S.logoImage ? ' has-img' : ''}" href="${esc(ROOT_URL)}" aria-label="${esc(S.logoText)}">${logo}</a>
  <div class="nav-right">
    <button class="icon-btn" id="cartBtn" aria-label="Cart">
      <svg viewBox="0 0 24 24"><path d="M5 8h14l-1.2 12.2a1 1 0 0 1-1 .8H7.2a1 1 0 0 1-1-.8z"/><path d="M9 10V6a3 3 0 0 1 6 0v4"/></svg>
      <span class="badge" id="badge">0</span>
    </button>
  </div>
</header>
<canvas id="stage" aria-label="Sculpture shelf"></canvas>
<div id="vignette"></div>
<div id="labels"></div>
<div class="hint" id="hint">${esc(S.hint)}</div>
${PRODUCTS.length ? '' : `<div class="hint on" style="top:45%">No products yet — pick a collection in the theme editor</div>`}
<footer>
  <div class="coll"><h2>${esc(S.heading)}</h2><p>${esc(S.subheading)}</p></div>
  <div class="pager"><div class="count" id="count"></div></div>
  <div class="ship">${esc(S.note1)}<br>${esc(S.note2)}</div>
</footer>
<section id="pd" aria-hidden="true" role="dialog" aria-label="Product">
  <div id="viewer"><div class="tip">Drag to rotate · Pinch to zoom</div></div>
  <div class="info">
    <div class="kicker" id="pdKicker"></div>
    <h1 id="pdName"></h1>
    <p class="epithet" id="pdEpithet"></p>
    <div class="price" id="pdPrice"></div>
    <div class="tiers" id="pdTiers"></div>
    <div id="pdFinishWrap"><div class="opt-label"><span id="pdFinishName">Finish</span><span id="pdStone"></span></div><div class="swatches" id="pdSwatches"></div></div>
    <div id="pdSizeWrap"><div class="opt-label"><span id="pdSizeName">Height</span><span id="pdSize"></span></div><div class="sizes" id="pdSizes"></div></div>
    <button class="add" id="pdAdd">Add to cart</button>
    <div class="desc" id="pdDesc"></div>
    <ul class="specs" id="pdSpecs"></ul>
  </div>
  <button class="close" id="pdClose" aria-label="Close"><svg viewBox="0 0 24 24"><path d="M5 5l14 14M19 5L5 19"/></svg></button>
</section>
<div id="scrim"></div>
<aside id="cart" aria-hidden="true" aria-label="Cart">
  <div class="cart-head"><h3>Your Cart</h3><button class="icon-btn" id="cartClose" aria-label="Close cart"><svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"/></svg></button></div>
  <div class="cart-items" id="cartItems"></div>
  <div class="cart-foot">
    <div class="nudge" id="nudge"></div>
    <div id="discounts"></div>
    <div class="row"><span>Subtotal</span><span id="subtotal"></span></div>
    <div class="row muted"><span>Shipping &amp; taxes calculated at checkout</span></div>
    <button class="add" id="checkout">Checkout</button>
  </div>
</aside>
<div id="toast"></div>`;
const $ = id => document.getElementById(id);

/* =========================================================
   3D MODELS — real GLB from Shopify product media, else stand-in
   ========================================================= */
const gltf = new GLTFLoader();
const draco = new DRACOLoader(); draco.setDecoderPath('https://cdn.jsdelivr.net/npm/three@0.170.0/examples/jsm/libs/draco/gltf/');
gltf.setDRACOLoader(draco);
const modelCache = {};
function boxUV(geo){
  // printed-model GLBs often have no UVs — project them so the stone texture still maps
  if (!geo.attributes.normal) geo.computeVertexNormals();
  geo.computeBoundingBox();
  const s = geo.boundingBox.getSize(new THREE.Vector3()), k = 2 / Math.max(s.x, s.y, s.z);
  const p = geo.attributes.position, n = geo.attributes.normal, uv = new Float32Array(p.count * 2);
  for (let i = 0; i < p.count; i++) {
    const ax = Math.abs(n.getX(i)), ay = Math.abs(n.getY(i)), az = Math.abs(n.getZ(i));
    const x = p.getX(i) * k, y = p.getY(i) * k, z = p.getZ(i) * k;
    const [u, v] = ay >= ax && ay >= az ? [x, z] : az >= ax ? [x, y] : [z, y];
    uv[i * 2] = u; uv[i * 2 + 1] = v;
  }
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
}
function loadModel(url){
  return modelCache[url] ||= new Promise((res, rej) => gltf.load(url, g => {
    const obj = g.scene, wrap = new THREE.Group();
    const box = new THREE.Box3().setFromObject(obj), size = box.getSize(new THREE.Vector3()), c = box.getCenter(new THREE.Vector3());
    obj.position.set(-c.x, -box.min.y, -c.z);
    // fit inside a 1.75 tall × 0.95 wide slot so wide pieces (busts, reclining figures) don't crowd neighbours
    wrap.add(obj); wrap.scale.setScalar(Math.min(1.75 / (size.y || 1), .95 / (Math.max(size.x, size.z) || 1)));
    obj.traverse(m => { if (m.isMesh) { m.castShadow = m.receiveShadow = true; if (S.stoneOverride && !m.geometry.attributes.uv) boxUV(m.geometry); } });
    res(wrap);
  }, undefined, rej));
}
function applyStone(obj, key){
  if (!S.stoneOverride) return obj;
  const mat = stoneMaterial(key);
  obj.traverse(m => { if (m.isMesh) m.material = mat; });
  return obj;
}
// returns a Group immediately; real model swaps in when loaded
function makeStatue(p, key){
  const holder = new THREE.Group();
  if (p.model) {
    loadModel(p.model).then(m => { holder.clear(); holder.add(applyStone(m.clone(true), key)); })
      .catch(() => { holder.clear(); holder.add(buildGoddess(p.pose, stoneMaterial(key))); });
  } else holder.add(buildGoddess(p.pose, stoneMaterial(key)));
  return holder;
}

/* =========================================================
   MAIN SHELF SCENE
   ========================================================= */
const canvas = $('stage');
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;

const scene = new THREE.Scene();
scene.environment = new THREE.PMREMGenerator(renderer).fromScene(new RoomEnvironment(), .04).texture;
scene.environmentIntensity = .28;
scene.background = new THREE.Color(0x0a0908);
const camera = new THREE.PerspectiveCamera(28, 1, .1, 60);
const N = PRODUCTS.length, LAST = Math.max(0, N - 1) * SPACING;

// wall — lamp-black plaster
const plaster = plasterWall();
plaster.map.repeat.set(30, 11); plaster.bump.repeat.set(30, 11);
const wall = new THREE.Mesh(new THREE.PlaneGeometry(80, 30),
  new THREE.MeshStandardMaterial({ map: plaster.map, bumpMap: plaster.bump, bumpScale: 1.4, roughness: .96, envMapIntensity: .25 }));
wall.position.set(LAST / 2, 4, -.42); wall.receiveShadow = true; scene.add(wall);

// shelf — thick ancient oak on carved corbels
const shelfW = LAST + 30, wood = ancientWood();
const woodMat = (rx, ry) => {
  const map = wood.map.clone(), bump = wood.bump.clone();
  map.repeat.set(rx, ry); bump.repeat.set(rx, ry); map.needsUpdate = bump.needsUpdate = true;
  return new THREE.MeshStandardMaterial({ map, bumpMap: bump, bumpScale: 3.2, roughness: .82, envMapIntensity: .45 });
};
function plankUV(geo, su = 2.6, sv = .9){
  const p = geo.attributes.position, n = geo.attributes.normal, uv = geo.attributes.uv;
  for (let i = 0; i < p.count; i++) {
    const ax = Math.abs(n.getX(i)), ay = Math.abs(n.getY(i)), az = Math.abs(n.getZ(i));
    const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
    if (ay >= ax && ay >= az) uv.setXY(i, x / su, z / sv + .37);
    else if (az >= ax) uv.setXY(i, x / su, y / sv + .11);
    else uv.setXY(i, z / su, y / sv);
  }
  uv.needsUpdate = true; return geo;
}
const slab = new THREE.Mesh(plankUV(new RoundedBoxGeometry(shelfW, .19, .8, 6, .04)), woodMat(1, 1));
slab.position.set(LAST / 2, -.095, -.02);
const trim = new THREE.Mesh(plankUV(new RoundedBoxGeometry(shelfW, .07, .7, 4, .022), 2.1, .7), woodMat(1, 1));
trim.position.set(LAST / 2, -.222, -.07);
for (const m of [slab, trim]) { m.castShadow = m.receiveShadow = true; scene.add(m); }
const cs = new THREE.Shape();
cs.moveTo(0, 0); cs.lineTo(.5, 0); cs.lineTo(.5, -.05);
cs.bezierCurveTo(.49, -.13, .4, -.15, .33, -.17);
cs.bezierCurveTo(.22, -.2, .16, -.27, .14, -.42);
cs.quadraticCurveTo(.12, -.5, .05, -.52); cs.lineTo(0, -.52); cs.lineTo(0, 0);
const corbelGeo = new THREE.ExtrudeGeometry(cs, { depth: .11, bevelEnabled: true, bevelThickness: .012, bevelSize: .012, bevelSegments: 3, curveSegments: 28 });
const corbelMat = woodMat(1.6, 1.6);
for (let x = -3 * SPACING; x <= LAST + 2.5 * SPACING; x += SPACING) {
  const cb = new THREE.Mesh(corbelGeo, corbelMat);
  cb.rotation.y = -Math.PI / 2; cb.position.set(x + SPACING / 2 + .055, -.257, -.42);
  cb.castShadow = cb.receiveShadow = true; scene.add(cb);
}

// lights
scene.add(new THREE.HemisphereLight(0xfff4e6, 0x1a1410, .1));
const key = new THREE.DirectionalLight(0xffe8cc, .75);
key.position.set(LAST / 2 + 1.5, 5, 4); key.target.position.set(LAST / 2, 0, 0);
key.castShadow = true; key.shadow.mapSize.set(4096, 2048);
Object.assign(key.shadow.camera, { left: -LAST / 2 - 3, right: LAST / 2 + 3, top: 3, bottom: -1.5, near: .5, far: 14 });
key.shadow.bias = -.0004; key.shadow.normalBias = .02; key.shadow.radius = 5;
scene.add(key, key.target);
const fill = new THREE.DirectionalLight(0xffcf9a, 1.1);
fill.position.set(LAST / 2, -.6, 6); fill.target.position.set(LAST / 2, -.15, 0); scene.add(fill, fill.target);

const statues = [], spots = [];
PRODUCTS.forEach((p, i) => {
  const s = makeStatue(p, defaultStone(p));
  s.position.set(i * SPACING, 0, .02); s.userData.index = i;
  scene.add(s); statues.push(s);
  const sp = new THREE.SpotLight(0xffe9cc, 9, 6, .42, .85, 1.6);
  sp.position.set(i * SPACING, 3.1, .9); sp.target.position.set(i * SPACING, .7, 0);
  scene.add(sp, sp.target); spots.push(sp);
});
const plates = PRODUCTS.map(p => {
  const d = document.createElement('div'); d.className = 'plate';
  d.innerHTML = `<b>${esc(p.title)}</b><i>${new Set(p.variants.map(v => v.price)).size > 1 ? 'From ' : ''}${money(p.minPrice)}</i>`;
  $('labels').appendChild(d); return d;
});
renderer.toneMappingExposure = 1.0;

/* ---------- camera fit ---------- */
let camDist = 6, camY = .64;
function resize(){
  const w = innerWidth, h = innerHeight, aspect = w / h;
  renderer.setSize(w, h, false); camera.aspect = aspect;
  const tanH = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) * 2;
  // phones (portrait): frame one statue large, with only a hint of the next one
  const wantW = aspect < 1 ? 1.5 : Math.min(5.6, 2.2 + aspect * 1.9);
  const wantH = aspect < 1 ? 2.9 : 2.8;
  camDist = Math.max(wantW / (tanH * aspect), wantH / tanH);
  camY = aspect < 1 ? .62 : .64;
  camera.updateProjectionMatrix();
  const gapPx = SPACING / (tanH * camDist * aspect) * w;
  document.documentElement.style.setProperty('--plate', Math.max(11, Math.min(20, gapPx * .072)) + 'px');
}
addEventListener('resize', resize); resize();

/* ---------- navigation ---------- */
let targetX = 0, curX = 0, dragging = false, moved = 0, lastPX = 0, startPX = 0, vel = 0, lastT = 0;
const clampX = x => Math.max(-.15, Math.min(LAST + .15, x));
const worldPerPx = () => (Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) * 2 * camDist) / innerHeight;
const snap = () => { targetX = Math.round(Math.max(0, Math.min(LAST, targetX)) / SPACING) * SPACING; };
const go = i => { targetX = Math.max(0, Math.min(N - 1, i)) * SPACING; };
const active = () => Math.round(Math.max(0, Math.min(LAST, curX)) / SPACING);
const targetIdx = () => Math.round(Math.max(0, Math.min(LAST, targetX)) / SPACING);
const ray = new THREE.Raycaster(), ndc = new THREE.Vector2();
let hovered = -1;
function pick(e){
  const r = canvas.getBoundingClientRect();
  ndc.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
  ray.setFromCamera(ndc, camera);
  const hit = ray.intersectObjects(statues, true)[0];
  if (!hit) return -1;
  let o = hit.object; while (o && o.userData.index === undefined) o = o.parent;
  return o ? o.userData.index : -1;
}
const hint = $('hint'), hideHint = () => hint.classList.remove('on');
canvas.addEventListener('pointerdown', e => {
  dragging = true; moved = 0; lastPX = startPX = e.clientX; vel = 0; lastT = performance.now();
  canvas.setPointerCapture(e.pointerId); canvas.classList.add('dragging'); hideHint();
});
canvas.addEventListener('pointermove', e => {
  if (dragging) {
    const dx = e.clientX - lastPX, now = performance.now();
    moved = Math.max(moved, Math.abs(e.clientX - startPX));
    targetX = clampX(targetX - dx * worldPerPx());
    vel = -dx * worldPerPx() / Math.max(1, now - lastT) * 16; lastT = now; lastPX = e.clientX;
  } else if (e.pointerType === 'mouse') {
    hovered = pick(e); canvas.classList.toggle('hovering', hovered >= 0);
  }
});
const endDrag = e => {
  if (!dragging) return; dragging = false; canvas.classList.remove('dragging');
  if (moved < 6) { const i = pick(e); if (i >= 0) { if (i === active() || innerWidth > 820) openProduct(i); else go(i); } }
  else { targetX += vel * 6; snap(); }
};
canvas.addEventListener('pointerup', endDrag);
canvas.addEventListener('pointercancel', endDrag);
canvas.addEventListener('pointerleave', () => { hovered = -1; });
let wheelT;
addEventListener('wheel', e => {
  if (pdOpen || cartOpen) return;
  e.preventDefault(); hideHint();
  targetX = clampX(targetX + (Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY) * .0028);
  clearTimeout(wheelT); wheelT = setTimeout(snap, 140);
}, { passive: false });
addEventListener('keydown', e => {
  if (e.key === 'Escape') { closeProduct(); closeCart(); }
  if (pdOpen || cartOpen) return;
  if (e.key === 'ArrowRight') go(targetIdx() + 1);
  if (e.key === 'ArrowLeft') go(targetIdx() - 1);
  if (e.key === 'Enter' && N) openProduct(targetIdx());
});

/* ---------- loop ---------- */
const countEl = $('count'), tmp = new THREE.Vector3(), clock = new THREE.Clock();
let lastActive = -1;
function frame(){
  requestAnimationFrame(frame);
  const dt = Math.min(clock.getDelta(), .05), t = clock.elapsedTime;
  if (pdOpen) { controls.update(); vr.render(vs, vcam); return; }
  curX += (targetX - curX) * (1 - Math.pow(dragging ? .00001 : .0015, dt));
  camera.position.set(curX, camY + .35, camDist);
  camera.lookAt(curX, camY, 0);
  statues.forEach((s, i) => {
    const want = hovered === i ? .55 : Math.sin(t * .25 + i) * .06;
    s.rotation.y += (want - s.rotation.y) * (1 - Math.pow(.02, dt));
    const near = 1 - Math.min(1, Math.abs(i * SPACING - curX) / (SPACING * 2.2));
    spots[i].intensity = 2 + near * 9 + (hovered === i ? 5 : 0);
    tmp.set(i * SPACING, -.33, .4).project(camera);
    plates[i].style.transform = `translate(${(tmp.x * .5 + .5) * innerWidth}px,${(-tmp.y * .5 + .5) * innerHeight}px) translate(-50%,0)`;
    plates[i].style.opacity = (.25 + near * .75).toFixed(2);
  });
  const a = active();
  if (N && a !== lastActive) { lastActive = a; countEl.textContent = `${pad(a + 1)} / ${pad(N)}`; }
  renderer.render(scene, camera);
}

/* =========================================================
   PRODUCT VIEWER
   ========================================================= */
const pd = $('pd'), viewerEl = $('viewer');
const vr = new THREE.WebGLRenderer({ antialias: true, alpha: true });
vr.setPixelRatio(Math.min(devicePixelRatio, 2));
vr.toneMapping = THREE.ACESFilmicToneMapping; vr.shadowMap.enabled = true; vr.shadowMap.type = THREE.PCFSoftShadowMap;
viewerEl.prepend(vr.domElement);
const vs = new THREE.Scene();
vs.environment = new THREE.PMREMGenerator(vr).fromScene(new RoomEnvironment(), .04).texture;
vs.environmentIntensity = .7;
const vcam = new THREE.PerspectiveCamera(30, 1, .05, 40);
const controls = new OrbitControls(vcam, vr.domElement);
Object.assign(controls, { enableDamping: true, dampingFactor: .07, enablePan: false, minDistance: 1.4, maxDistance: 5.5,
  autoRotate: true, autoRotateSpeed: .9, minPolarAngle: .35, maxPolarAngle: Math.PI / 2 + .05 });
controls.target.set(0, .8, 0);
controls.addEventListener('start', () => controls.autoRotate = false);
const vkey = new THREE.DirectionalLight(0xfff1de, 2.2); vkey.position.set(2, 4, 3); vkey.castShadow = true;
vkey.shadow.mapSize.set(2048, 2048); Object.assign(vkey.shadow.camera, { left: -1.2, right: 1.2, top: 2.2, bottom: -.5 }); vkey.shadow.radius = 6;
const vrim = new THREE.DirectionalLight(0xdfe8ff, 1.4); vrim.position.set(-3, 2.5, -2.5);
vs.add(vkey, vrim, new THREE.HemisphereLight(0xffffff, 0x222222, .35));
const ground = new THREE.Mesh(new THREE.CircleGeometry(3, 64), new THREE.ShadowMaterial({ opacity: .28 }));
ground.rotation.x = -Math.PI / 2; ground.receiveShadow = true; vs.add(ground);

let pdOpen = false, cur = null, model = null;
function sizeViewer(){
  const r = viewerEl.getBoundingClientRect(); if (!r.width) return;
  vr.setSize(r.width, r.height, false); vcam.aspect = r.width / r.height; vcam.updateProjectionMatrix();
}
addEventListener('resize', () => pdOpen && sizeViewer());
function setModel(){
  if (model) vs.remove(model);
  const p = PRODUCTS[cur.i];
  model = makeStatue(p, p.finishes.length ? stoneKey(cur.finish) : defaultStone(p)); vs.add(model);
}
function specsFor(p, v){
  if (p.specs) return p.specs.split(/\n+/).map(l => l.split(/:\s*/)).filter(x => x[0].trim());
  return [
    ...(p.fi >= 0 ? [['Finish', cur.finish]] : []), ...(p.si >= 0 ? [['Size', cur.size]] : []),
    ['Material', 'PLA+ filament'], ['Finish', 'Hand-applied mineral stone coat'],
    ['Surface', 'Matte, chalky stone texture'], ['Shipping', 'Free within the U.S.'],
    ['Availability', v.available ? 'Made to order' : 'Sold out'],
  ];
}
function renderOptions(){
  const p = PRODUCTS[cur.i], v = findVariant(p, cur.finish, cur.size);
  cur.variant = v;
  $('pdPrice').innerHTML = `${money(v.price)}${v.compare_at_price > v.price ? ` <s style="opacity:.45;font-size:15px">${money(v.compare_at_price)}</s>` : ''}<small>${esc(S.priceNote)}</small>`;
  // plain numbers (no currency sign); savings measured against buying singles
  const num = c => (c / 100).toLocaleString('en-US', { minimumFractionDigits: c % 100 ? 2 : 0, maximumFractionDigits: 2 });
  const single = PCT ? v.price : (TIERS[0]?.each || 0);
  $('pdTiers').style.gridTemplateColumns = `repeat(${TIERS.length},1fr)`;
  $('pdTiers').innerHTML = TIERS.length > 1 ? TIERS.map((t, k) => {
    const total = tierTotal(t, v.price), save = single * t.n - total;
    return `<div class="tier t${k + 1}${k === TIERS.length - 1 ? ' best' : ''}">
      ${k === TIERS.length - 1 ? '<em>Best value</em>' : ''}
      <span>${esc(t.label)}</span>${PCT ? `<span class="pct">${t.pct > 0 ? `${t.pct}% off` : 'Full price'}</span>` : ''}<b>${num(total)}</b>
      <i>You pay ${num(Math.round(total / t.n))} each</i>
      <i class="${save > 0 ? 'save' : ''}">${save > 0 ? `You save ${num(save)}` : 'Single piece'}</i>
    </div>`;
  }).join('') + (S.tiersNote ? `<p class="tiers-note">${esc(S.tiersNote)}</p>` : '') : '';
  $('pdFinishWrap').style.display = p.fi < 0 ? 'none' : '';
  $('pdSizeWrap').style.display = p.si < 0 ? 'none' : '';
  if (p.fi >= 0) {
    $('pdFinishName').textContent = p.options[p.fi]; $('pdStone').textContent = cur.finish;
    $('pdSwatches').innerHTML = p.finishes.map((f, k) =>
      `<button class="sw ${f === cur.finish ? 'on' : ''}" data-k="${k}" title="${esc(f)}" aria-label="${esc(f)}" style="background-image:url(${stoneTextures(stoneKey(f)).url})"></button>`).join('');
    $('pdSwatches').querySelectorAll('.sw').forEach(b => b.onclick = () => { cur.finish = p.finishes[+b.dataset.k]; setModel(); renderOptions(); });
  }
  if (p.si >= 0) {
    $('pdSizeName').textContent = p.options[p.si]; $('pdSize').textContent = cur.size;
    $('pdSizes').style.gridTemplateColumns = `repeat(${Math.min(p.sizes.length, 3)},1fr)`;
    $('pdSizes').innerHTML = p.sizes.map((s, k) => {
      const sv = findVariant(p, cur.finish, s);
      return `<button class="size ${s === cur.size ? 'on' : ''}" data-k="${k}"><b>${esc(s)}</b><i>${sv.available ? money(sv.price) : 'Sold out'}</i></button>`;
    }).join('');
    $('pdSizes').querySelectorAll('.size').forEach(b => b.onclick = () => { cur.size = p.sizes[+b.dataset.k]; renderOptions(); });
  }
  $('pdAdd').disabled = !v.available;
  $('pdAdd').textContent = v.available ? 'Add to cart' : 'Sold out';
  $('pdAdd').style.opacity = v.available ? '' : '.4';
  $('pdSpecs').innerHTML = specsFor(p, v).map(([a, b]) => `<li><span>${esc(a)}</span><span>${esc(b)}</span></li>`).join('');
}
function openProduct(i, push = true){
  const p = PRODUCTS[i];
  const first = p.variants.find(v => v.available) || p.variants[0];
  cur = { i, finish: p.fi >= 0 ? first.options[p.fi] : null, size: p.si >= 0 ? first.options[p.si] : null };
  $('pdKicker').textContent = `${S.heading} · No. ${pad(i + 1)}`;
  $('pdName').textContent = p.title; $('pdEpithet').textContent = p.epithet || '';
  $('pdDesc').innerHTML = p.description_html || '';
  renderOptions(); setModel();
  vcam.position.set(0, 1.2, innerWidth < 820 ? 4.6 : 4.3); controls.autoRotate = true; controls.update();
  pdOpen = true; pd.classList.add('open'); pd.setAttribute('aria-hidden', 'false');
  pd.querySelector('.info').scrollTop = 0;
  requestAnimationFrame(sizeViewer);
  hovered = -1; canvas.classList.remove('hovering');
  go(i); curX = targetX;
  if (push && p.url && location.pathname !== p.url) history.replaceState({}, '', p.url);
  document.title = `${p.title} — ${DATA.shopName}`;
}
function closeProduct(){
  if (!pdOpen) return; pdOpen = false; pd.classList.remove('open'); pd.setAttribute('aria-hidden', 'true');
  if (DATA.baseUrl && location.pathname !== DATA.baseUrl) history.replaceState({}, '', DATA.baseUrl);
  document.title = DATA.baseTitle;
}
$('pdClose').onclick = closeProduct;

/* =========================================================
   CART — Shopify Ajax Cart API
   ========================================================= */
let cartOpen = false, cartData = { items: [], item_count: 0, total_price: 0 };
const api = (path, body) => fetch(ROOT_URL + path, body ? {
  method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body),
} : { headers: { Accept: 'application/json' } }).then(async r => {
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.description || j.message || 'Something went wrong');
  return j;
});
async function refreshCart(){ try { cartData = await api('cart.js'); } catch (e) {} renderCart(); }
function renderCart(){
  const c = cartData;
  $('badge').textContent = c.item_count; $('badge').classList.toggle('on', c.item_count > 0);
  $('subtotal').textContent = money(c.total_price || 0);
  const disc = (c.cart_level_discount_applications || []).filter(d => d.total_allocated_amount > 0);
  $('discounts').innerHTML = disc.map(d => `<div class="row save"><span>${esc(d.title)}</span><span>−${money(d.total_allocated_amount)}</span></div>`).join('');
  const next = TIERS.find(t => t.n > c.item_count);
  $('nudge').innerHTML = c.item_count && next
    ? PCT ? `Add ${next.n - c.item_count} more and get <b>${next.pct}% off</b> every piece in your order, with free shipping.`
          : `Add ${next.n - c.item_count} more and get ${next.n} for <b>${money(next.cents)}</b>, just ${money(next.each)} each, with free shipping.`
    : c.item_count >= (TIERS.at(-1)?.n || Infinity) ? 'You’ve unlocked the best price per sculpture.' : '';
  $('nudge').style.display = $('nudge').innerHTML ? '' : 'none';
  $('cartItems').innerHTML = c.items.length ? c.items.map(it => `
    <div class="ci">
      <div class="thumb" style="background-image:url('${esc(it.image ? it.image + (it.image.includes('?') ? '&' : '?') + 'width=200' : '')}')"></div>
      <div><h4>${esc(it.product_title)}</h4><p>${esc(it.variant_title && it.variant_title !== 'Default Title' ? it.variant_title : '')}</p>
        <div class="qty"><button data-key="${esc(it.key)}" data-q="${it.quantity - 1}" aria-label="Decrease">−</button><span>${it.quantity}</span><button data-key="${esc(it.key)}" data-q="${it.quantity + 1}" aria-label="Increase">+</button></div></div>
      <div class="p">${money(it.final_line_price)}</div>
    </div>`).join('') : '<div class="empty">Your cart is empty.</div>';
  $('cartItems').querySelectorAll('.qty button').forEach(b => b.onclick = async () => {
    b.disabled = true;
    try { cartData = await api('cart/change.js', { id: b.dataset.key, quantity: +b.dataset.q }); } catch (e) { toast(e.message); }
    renderCart();
  });
}
function openCart(){ cartOpen = true; refreshCart(); $('cart').classList.add('open'); $('scrim').classList.add('open'); $('cart').setAttribute('aria-hidden', 'false'); }
function closeCart(){ cartOpen = false; $('cart').classList.remove('open'); $('scrim').classList.remove('open'); $('cart').setAttribute('aria-hidden', 'true'); }
$('cartBtn').onclick = openCart; $('cartClose').onclick = closeCart; $('scrim').onclick = closeCart;
$('pdAdd').onclick = async () => {
  const btn = $('pdAdd'); if (!cur.variant?.available) return;
  btn.disabled = true; btn.textContent = 'Adding…';
  try {
    await api('cart/add.js', { items: [{ id: cur.variant.id, quantity: 1 }] });
    await refreshCart(); toast(`${PRODUCTS[cur.i].title} added to cart`);
  } catch (e) { toast(e.message); }
  renderOptions(); btn.disabled = false;
};
$('checkout').onclick = () => { if (cartData.item_count) location.href = ROOT_URL + 'checkout'; else toast('Your cart is empty'); };
let toastT;
function toast(msg){ const t = $('toast'); t.textContent = msg; t.classList.add('on'); clearTimeout(toastT); toastT = setTimeout(() => t.classList.remove('on'), 2400); }

/* ---------- go ---------- */
refreshCart();
const openIdx = PRODUCTS.findIndex(p => p.handle === DATA.openHandle);
if (openIdx >= 0) openProduct(openIdx, false);
frame();
setTimeout(() => {
  $('loader').classList.add('done');
  if (openIdx < 0 && N) { setTimeout(() => hint.classList.add('on'), 1400); setTimeout(hideHint, 6000); }
}, 250);
